
import React, { useEffect } from "react";
import styles from "./SpitiValley.module.css";
import BlogNavigator from "../../../BlogNavigator/BlogNavigator.js";
import PromoSidebar from '../../../PromoSidebar/PromoSidebar';
import CTASection from "../../../../CTASection/CTASection";


import { travelBlogs } from "../TravelCategory.js";
import img1 from "./Spiti Valley.webp";
import img2 from "./Key Monastery.webp";
import img3 from "./Chandratal Lake.webp";
import img4 from "./Komic Village.webp";
import img5 from "./Dhankar Monastery.webp";
import img6 from "./Kibber Village.webp";

export default function SpitiBlogPage() {
  useEffect(() => {
    const elems = document.querySelectorAll(`.${styles.reveal}`);
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.inView);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    elems.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <main className={styles.spitiPage}>
      {/* HERO */}
      <header className={styles.heroSection}>
        <img src={img1} alt="Spiti Valley panorama" className={styles.heroBg} />
        <div className={styles.heroText}>
          <h1 className={styles.heroh1}>Spiti Valley — A Soulful Escape Where Silence Speaks</h1>
          <p className={styles.heroSub}>
            Experience the untouched beauty of Himachal’s cold desert — where every road tells a story.
          </p>
        </div>
        <div className={styles.heroOverlay} aria-hidden="true" />
      </header>

      <section className={`${styles.contentWrapper} ${styles.container}`}>
        {/* INTRO */}
        <section className={`${styles.intro} ${styles.reveal}`}>
          <h2>Where the Mountains Whisper Peace</h2>
          <p>
            Hidden deep within the majestic <strong>Himalayas</strong>, the <strong>Spiti Valley</strong> is a land where silence speaks louder than words. 
            Framed by rugged peaks and ancient <strong>Buddhist monasteries</strong>, every turn of the road unveils breathtaking beauty and spiritual calm. 
            Here, the chill winds carry stories of faith, freedom, and timeless charm — making <strong>Spiti</strong> not just a place to visit, but a journey to the soul.
          </p>
        </section>

        {/* HIGHLIGHTS */}
        <div className={styles.highlightGrid}>
          <article className={`${styles.highlightCard} ${styles.reveal}`}>
            <img src={img2} alt="Key Monastery, Spiti" className={styles.cardImg} />
            <div className={styles.cardBody}>
              <h3>Key Monastery</h3>
              <p>Perched on a rugged hill, <strong>Key Monastery</strong> is the spiritual heart of <strong>Spiti Valley</strong>. As dawn breaks, golden light meets monks’ chants, filling the valley with peace and devotion.</p>
            </div>
          </article>

          <article className={`${styles.highlightCard} ${styles.reveal}`}>
            <img src={img3} alt="Chandratal Lake" className={styles.cardImg} />
            <div className={styles.cardBody}>
              <h3>Chandratal Lake</h3>
              <p>High in the Himalayas, <strong>Chandratal Lake</strong> — the “Lake of the Moon” — shimmers like a sapphire mirror, reflecting snow peaks and capturing every traveler’s heart.</p>
            </div>
          </article>

          <article className={`${styles.highlightCard} ${styles.reveal}`}>
            <img src={img4} alt="Komic Village" className={styles.cardImg} />
            <div className={styles.cardBody}>
              <h3>Village Life</h3>
              <p>Experience authentic <strong>Spiti village life</strong> — warm smiles, ancient mud homes, and traditions that echo through the Himalayas, where every day unfolds in harmony with nature.</p>
            </div>
          </article>

          <article className={`${styles.highlightCard} ${styles.reveal}`}>
            <img src={img5} alt="Dhankar Monastery" className={styles.cardImg} />
            <div className={styles.cardBody}>
              <h3>Epic Road Trips</h3>
              <p>Embark on an unforgettable <strong>Spiti Valley road trip</strong> — where every curve reveals breathtaking peaks, ancient villages, and the raw thrill of the Himalayas.</p>
            </div>
          </article>
        </div>
      </section>

      {/* MAIN CONTENT + SIDEBAR */}
      <div className={styles.mainFlex}>
        <main className={styles.articleArea}>
          <section className={`${styles.experience} ${styles.reveal}`}>
            <div className={styles.experienceRow}>
              <div className={styles.text}>
                <h2>Culture & Spirituality</h2>
                <img src={img6} alt="Spiti monks" className={styles.expImg} />
                <p>
                  Tucked away in the mystical <strong>Himalayas</strong>, <strong>Spiti Valley</strong> is a land where time slows down and the soul finds peace. 
                  Its ancient <strong>Buddhist monasteries</strong> — like <strong>Key Monastery</strong> and <strong>Dhankar Gompa</strong> — stand as living symbols of faith and serenity.
                  The fluttering <strong>prayer flags</strong> carry silent wishes across the wind, reminding every traveler of life’s impermanence and beauty.
                </p>
              </div>
            </div>
          </section>

          <section className={`${styles.experience} ${styles.reveal}`}>
            <div className={styles.experienceRow}>
              <div className={styles.text}>
                <h2>Adventure & Exploration</h2>
                <img src={img5} alt="Spiti trekking" className={styles.expImg} />
                <p>
                  For those who crave thrill and discovery, <strong>Spiti Valley</strong> is an adventurer’s paradise. From <strong>epic high-altitude road trips</strong> to <strong>trekking across glaciers</strong> and hidden villages, every turn brings heart-pounding beauty.
                </p>
              </div>
            </div>
          </section>

          <section id="tips" className={`${styles.travelTips} ${styles.reveal}`}>
            <h2>Essential Travel Tips for Spiti Valley</h2>
            <ul>
              <li><strong>Pack warm layers:</strong> Even during summer, nights can drop below freezing.</li>
              <li><strong>Acclimatize slowly:</strong> Altitude ranges 10,000–15,000 ft. Rest well.</li>
              <li><strong>Carry cash:</strong> ATMs are scarce beyond Kaza.</li>
              <li><strong>Power backup:</strong> Carry power banks & solar chargers.</li>
              <li><strong>Offline maps:</strong> Network is patchy; download routes offline.</li>
            </ul>
          </section>
        </main>

        <div className={styles.sideCard}>
          <h3 className={styles.sidecardhead} >Quick Facts About Spiti Valley</h3>
          <ul>
            <li><strong>Location:</strong> Northern Himachal Pradesh</li>
            <li><strong>Altitude:</strong> 10,000–15,000 ft</li>
            <li><strong>Best Season:</strong> May–October</li>
            <li><strong>Famous For:</strong> Monasteries, lakes, stargazing</li>
          </ul>
             <aside className={styles.rightSidebar}>
          <PromoSidebar />
        </aside>
        </div>
       
      </div>
<CTASection
  heading="Inspired to Explore the Heart of the Himalayas?"
  description="Spiti’s raw beauty changes everyone who visits. If the blog moved you, imagine experiencing Spiti in person."
  buttonText="Start Your Spiti Journey"
  buttonLink="tel:+919876543210"
/>

      <BlogNavigator
        category="travel"
        blogs={travelBlogs}
        currentBlogId="spiti-valley-travel-story"
      />
    </main>
  );
}
